<script>
  import { onMount, onDestroy, tick } from "svelte";
  import { tourFunctions } from "../tour-functions";
  import Popup from "./Popup.svelte";
  import {
    scopeFormCss,
    watchSlateSubmitInlineStyles,
  } from "./slateFormUtils.js";

  export let embedCode;
  export let delay;
  export let redisplay;
  export let title;
  export let width = 80;
  export let css;
  export let tourReady = false;

  let formContent;
  let observer;
  let slateStyleCleanup = null;
  let redisplayTriggered = false;
  let delayTimeout = null;

  // Start hidden if delay is set, otherwise wait for UI to be visible
  let visible = false;

  function closePopup() {
    visible = false;
    tourFunctions.focus();
    if (redisplay && !userInteracted && !redisplayTriggered) {
      redisplayTriggered = true;
      setTimeout(async () => {
        visible = true;
        await tick();
        loadEmbedCode();
        registerInteractions();
      }, redisplay);
    }
  }

  function getWidth() {
    if (width <= 100) return `${width}vw`;
    return `${width}px`;
  }

  let interactivityEvents = ["click", "keydown", "submit", "input"];
  let cookieName = "userInteracted";
  let cookieExpiryDays = 30;
  let userInteracted = getCookie(cookieName);

  function setCookie(name, value, days) {
    let expires = "";
    if (days) {
      let date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  function getCookie(name) {
    let nameEQ = name + "=";
    let ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  function registerInteractions() {
    if (formContent) {
      interactivityEvents.forEach((event) => {
        formContent.addEventListener(event, handleInteraction, {
          capture: true,
        });
      });

      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            interactivityEvents.forEach((event) => {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1) {
                  node.addEventListener(event, handleInteraction, {
                    capture: true,
                  });
                }
              });
            });
          }
        });
      });

      observer.observe(formContent, { childList: true, subtree: true });
    }
  }

  function unregisterInteractions() {
    if (formContent) {
      interactivityEvents.forEach((event) => {
        formContent.removeEventListener(event, handleInteraction, {
          capture: true,
        });
      });

      if (observer) {
        observer.disconnect();
      }
    }
  }

  function handleInteraction() {
    userInteracted = true;
    setCookie(cookieName, true, cookieExpiryDays);
    unregisterInteractions();
  }

  function loadEmbedCode() {
    if (slateStyleCleanup) {
      slateStyleCleanup();
      slateStyleCleanup = null;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(embedCode, "text/html");
    const embedContainer = document.getElementById("form-embed");
    embedContainer.innerHTML = "";

    doc.body.childNodes.forEach((node) => {
      if (node.nodeName.toLowerCase() === "script") {
        const script = document.createElement("script");

        if (node.src) {
          script.src = node.src;
        } else {
          script.textContent = node.textContent;
        }

        Array.from(node.attributes).forEach((attr) => {
          if (attr.name !== "src") {
            script.setAttribute(attr.name, attr.value);
          }
        });

        embedContainer.appendChild(script);
      } else {
        const newNode = document.importNode(node, true);
        embedContainer.appendChild(newNode);
      }
    });

    slateStyleCleanup = watchSlateSubmitInlineStyles(embedContainer);
  }

  function startDelayTimer() {
    if (redisplay && getCookie(cookieName)) {
      return;
    }

    if (!tourReady) {
      // Wait for tour to be ready before starting delay
      return;
    }

    if (delayTimeout) {
      clearTimeout(delayTimeout);
    }

    // If delay is set, wait for that duration after UI is visible
    // If delay is 0 or not set, show immediately after UI is visible
    const delayTime = delay || 0;
    
    delayTimeout = setTimeout(async () => {
      visible = true;
      await tick();
      loadEmbedCode();
      registerInteractions();
    }, delayTime);
  }

  // Start delay timer when tour becomes ready
  $: if (tourReady) {
    startDelayTimer();
  }

  onMount(() => {
    // If tour is already ready on mount, start the timer
    if (tourReady) {
      startDelayTimer();
    }
  });

  onDestroy(() => {
    unregisterInteractions();
    if (slateStyleCleanup) {
      slateStyleCleanup();
      slateStyleCleanup = null;
    }
    if (delayTimeout) {
      clearTimeout(delayTimeout);
    }
  });

  function getPopupTextColor() {
    // get --popup-text-color from the root element
    const rootStyles = getComputedStyle(document.documentElement);
    const popupTextColor = rootStyles.getPropertyValue(
      "--form-popup-text-color",
    );
    return popupTextColor || "#000";
  }

  function getPopupBgColor() {
    // get --popup-bg-color from the root element
    const rootStyles = getComputedStyle(document.documentElement);
    const popupBgColor = rootStyles.getPropertyValue(
      "--form-popup-bg-color",
    );
    return popupBgColor || "#fff";
  }

  $: useLegacyFormStyles = !css?.trim();
  $: embedCss = useLegacyFormStyles
    ? ""
    : `<style>${scopeFormCss(css)}</style>`;
</script>

<Popup 
  bind:visible
  on:close={closePopup}
  ariaLabel={title}
  width={getWidth()}
  textColor={getPopupTextColor()}
  bgColor={getPopupBgColor()}
>
  <h2 class="title">{title}</h2>
  <div
    class="form-content"
    class:legacy-form-styles={useLegacyFormStyles}
    bind:this={formContent}
  >
    <div id="form-embed"></div>
  </div>
</Popup>

{#if !useLegacyFormStyles && embedCss}{@html embedCss}{/if}

<style>
  .title {
    /* margin-bottom: 1em; */
    /* margin-left: 1em; */
    font-family: var(--font-heading), sans-serif;
    font-size: 2.5rem;
    line-height: 1;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
    box-sizing: border-box;
    width: 100%;
  }

  /* Legacy form styles — only when no custom form.css is saved */
  .legacy-form-styles :global(#form-embed h2) {
    font-size: 2em;
    font-family: var(--font-heading), sans-serif;
  }

  .legacy-form-styles :global(#form-embed .form_question) {
    padding: 0 0.5em;
  }

  .legacy-form-styles :global(#form-embed label) {
    margin-bottom: 0.5em;
    font-weight: 500;
  }

  .legacy-form-styles :global(#form-embed input[type="text"]),
  .legacy-form-styles :global(#form-embed input[type="email"]),
  .legacy-form-styles :global(#form-embed input[type="tel"]),
  .legacy-form-styles :global(#form-embed select),
  .legacy-form-styles :global(#form-embed textarea) {
    width: 100%;
    padding: 12px 20px;
    margin-bottom: 15px;
    box-sizing: border-box;
    border: 2px solid #aaa;
    border-radius: 5px;
    font-size: 16px;
    font-family: var(--font-body), sans-serif;
    outline: none;
  }

  .legacy-form-styles :global(#form-embed button),
  .legacy-form-styles :global(#form-embed input[type="submit"]),
  .legacy-form-styles :global(#form-embed .form_button_submit),
  .legacy-form-styles :global(#form-embed .default.form_button_submit) {
    padding: 10px 20px;
    background-color: var(--form-popup-text-color) !important;
    color: var(--form-popup-bg-color) !important;
    border: none;
    border-radius: 5px;
    font-size: 18px;
    font-family: var(--font-body), sans-serif;
    cursor: pointer;
    transition: background-color 0.3s ease;
    outline: none;
  }

  .legacy-form-styles :global(#form-embed button:hover),
  .legacy-form-styles :global(#form-embed input[type="submit"]:hover),
  .legacy-form-styles :global(#form-embed .form_button_submit:hover),
  .legacy-form-styles :global(#form-embed .default.form_button_submit:hover) {
    background-color: #666 !important;
  }

  .legacy-form-styles :global(#form-embed textarea) {
    min-height: 150px;
  }

  .legacy-form-styles :global(#form-embed input:focus-visible),
  .legacy-form-styles :global(#form-embed textarea:focus-visible),
  .legacy-form-styles :global(#form-embed select:focus-visible),
  .legacy-form-styles :global(#form-embed button:focus-visible) {
    outline: 4px solid var(--focus-color);
    outline-offset: 2px;
    border-radius: 0 !important;
  }

  /* Fixes for Slate Forms */
  :global(div.suggest) { 
    /* fixes suggestions appearing under the popup */
    z-index: 99999 !important;
  }
</style>
